import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api.js";
import type { CollectionItem } from "../lib/types.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { ConfirmDialog } from "../components/ConfirmDialog.js";

interface Props {
  projectId: string;
  focusId?: string | null;
}

export function NotesPanel({ projectId, focusId }: Props) {
  const toast = useToast();
  const [notes, setNotes] = useState<CollectionItem[] | null>(null);
  const [folders, setFolders] = useState<CollectionItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [folderFilter, setFolderFilter] = useState<string>("all");
  const [pendingDelete, setPendingDelete] = useState<CollectionItem | null>(null);

  const load = useCallback(async () => {
    const [notesRes, foldersRes] = await Promise.all([
      api.listCollection(projectId, "notes"),
      api.listCollection(projectId, "note-folders")
    ]);
    setNotes(notesRes.items);
    setFolders(foldersRes.items);
    setSelectedId((cur) => cur ?? focusId ?? notesRes.items[0]?.id ?? null);
  }, [projectId, focusId]);

  useEffect(() => {
    load().catch((err) => toast.error(err instanceof Error ? err.message : "Failed to load notes"));
  }, [load, toast]);

  useEffect(() => {
    if (focusId) setSelectedId(focusId);
  }, [focusId]);

  const folderName = (id: unknown) =>
    folders.find((f) => f.id === id)?.name as string | undefined;

  const filtered = useMemo(() => {
    if (!notes) return [];
    const q = query.trim().toLowerCase();
    return notes.filter((n) => {
      if (folderFilter !== "all" && (n.folderId ?? "none") !== folderFilter) return false;
      if (!q) return true;
      return (
        String(n.title ?? "").toLowerCase().includes(q) ||
        String(n.content ?? "").toLowerCase().includes(q) ||
        String(n.tags ?? "").toLowerCase().includes(q)
      );
    });
  }, [notes, query, folderFilter]);

  const selected = notes?.find((n) => n.id === selectedId) ?? null;

  const createNote = async () => {
    try {
      const { item } = await api.createCollectionItem(projectId, "notes", {
        title: "New Note",
        folderId: folderFilter !== "all" && folderFilter !== "none" ? folderFilter : null
      });
      setNotes((prev) => (prev ? [item, ...prev] : [item]));
      setSelectedId(item.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create note");
    }
  };

  const createFolder = async () => {
    const name = window.prompt("Folder name");
    if (!name?.trim()) return;
    try {
      const { item } = await api.createCollectionItem(projectId, "note-folders", { name: name.trim() });
      setFolders((prev) => [...prev, item]);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create folder");
    }
  };

  const update = async (id: string, data: Record<string, unknown>) => {
    try {
      const { item } = await api.updateCollectionItem("notes", id, data);
      setNotes((prev) => (prev ? prev.map((n) => (n.id === id ? item : n)) : prev));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    }
  };

  const togglePin = (note: CollectionItem) => update(note.id, { pinned: !note.pinned });

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const id = pendingDelete.id;
    setPendingDelete(null);
    try {
      await api.deleteCollectionItem("notes", id);
      setNotes((prev) => {
        const next = prev ? prev.filter((n) => n.id !== id) : prev;
        if (selectedId === id) setSelectedId(next?.[0]?.id ?? null);
        return next;
      });
      toast.success("Note deleted.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete");
    }
  };

  return (
    <div className="collection">
      <div className="collection-list">
        <div className="collection-list-head">
          <div className="collection-search">
            <Icon name="search" size={14} />
            <input className="collection-search-input" placeholder="Search notes…" value={query} onChange={(e) => setQuery(e.target.value)} />
          </div>
          <button className="btn btn-icon btn-primary btn-sm" title="New note" onClick={createNote}>
            <Icon name="plus" size={15} />
          </button>
        </div>
        <div className="notes-folderbar">
          <select className="select" value={folderFilter} onChange={(e) => setFolderFilter(e.target.value)}>
            <option value="all">All folders</option>
            <option value="none">Uncategorised</option>
            {folders.map((f) => (
              <option key={f.id} value={f.id}>{String(f.name)}</option>
            ))}
          </select>
          <button className="btn btn-icon btn-ghost btn-sm" title="New folder" onClick={createFolder}>
            <Icon name="plus" size={13} /><Icon name="note" size={13} />
          </button>
        </div>
        <div className="collection-items">
          {notes === null ? (
            <div className="collection-loading"><Icon name="clock" size={16} className="spin" /></div>
          ) : filtered.length === 0 ? (
            <div className="collection-empty-list">
              <p className="muted">{query ? "No matches." : "No notes yet."}</p>
              {!query && <button className="btn btn-sm" onClick={createNote}><Icon name="plus" size={14} /> New note</button>}
            </div>
          ) : (
            filtered.map((note) => (
              <button key={note.id} className={`collection-item ${note.id === selectedId ? "active" : ""}`} onClick={() => setSelectedId(note.id)}>
                <Icon name="note" size={14} className="collection-item-icon" />
                <div className="grow" style={{ minWidth: 0 }}>
                  <div className="truncate strong" style={{ fontSize: 13 }}>{String(note.title || "(untitled)")}</div>
                  <div className="truncate faint" style={{ fontSize: 11 }}>
                    {folderName(note.folderId) ?? "Uncategorised"}
                  </div>
                </div>
                {note.pinned ? <Icon name="check" size={12} style={{ color: "var(--accent)" }} /> : null}
              </button>
            ))
          )}
        </div>
      </div>

      <div className="collection-detail">
        {selected ? (
          <NoteEditor
            key={selected.id}
            note={selected}
            folders={folders}
            onSave={update}
            onTogglePin={() => togglePin(selected)}
            onDelete={() => setPendingDelete(selected)}
          />
        ) : (
          <div className="collection-detail-empty">
            <Icon name="note" size={40} style={{ color: "var(--text-faint)" }} />
            <h2>Notes</h2>
            <p className="muted">Freeform notes with folders, tags, and pinning.</p>
            <button className="btn btn-primary" onClick={createNote}><Icon name="plus" size={15} /> New note</button>
          </div>
        )}
      </div>

      {pendingDelete && (
        <ConfirmDialog
          title="Delete note"
          message={`Delete “${String(pendingDelete.title)}”? This cannot be undone.`}
          confirmLabel="Delete"
          danger
          onConfirm={confirmDelete}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
}

function NoteEditor({
  note,
  folders,
  onSave,
  onTogglePin,
  onDelete
}: {
  note: CollectionItem;
  folders: CollectionItem[];
  onSave: (id: string, data: Record<string, unknown>) => void;
  onTogglePin: () => void;
  onDelete: () => void;
}) {
  const [title, setTitle] = useState(String(note.title ?? ""));
  const [content, setContent] = useState(String(note.content ?? ""));
  const [tags, setTags] = useState(String(note.tags ?? ""));

  const commit = (key: string, value: string) => {
    if (String(note[key] ?? "") !== value) onSave(note.id, { [key]: value || null });
  };

  return (
    <div className="detail">
      <header className="detail-head">
        <input className="detail-title" value={title} onChange={(e) => setTitle(e.target.value)} onBlur={(e) => commit("title", e.target.value)} placeholder="Note title" />
        <div className="row gap-2">
          <button className={`btn btn-icon btn-sm ${note.pinned ? "btn-primary" : "btn-ghost"}`} title={note.pinned ? "Unpin" : "Pin"} onClick={onTogglePin}>
            <Icon name="check" size={14} />
          </button>
          <button className="btn btn-icon btn-ghost btn-danger btn-sm" title="Delete" onClick={onDelete}>
            <Icon name="trash" size={15} />
          </button>
        </div>
      </header>
      <div className="detail-body">
        <div className="row gap-3">
          <div className="field grow">
            <label className="label">Folder</label>
            <select className="select" value={String(note.folderId ?? "")} onChange={(e) => onSave(note.id, { folderId: e.target.value || null })}>
              <option value="">Uncategorised</option>
              {folders.map((f) => <option key={f.id} value={f.id}>{String(f.name)}</option>)}
            </select>
          </div>
          <div className="field grow">
            <label className="label">Tags</label>
            <input className="input" value={tags} placeholder="comma, separated" onChange={(e) => setTags(e.target.value)} onBlur={(e) => commit("tags", e.target.value)} />
          </div>
        </div>
        <div className="field">
          <label className="label">Content</label>
          <textarea className="textarea" rows={16} value={content} onChange={(e) => setContent(e.target.value)} onBlur={(e) => commit("content", e.target.value)} placeholder="Write freely…" />
        </div>
      </div>
    </div>
  );
}
